<template>
  <div class="min-h-screen" data-theme="green">
    <NavBar :signed-in="signedIn" :user-name="userName" active-page="shared_runs" />

    <!-- Shared Runs -->
    <div class="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div class="flex items-center justify-between mb-8">
        <h1 class="text-2xl font-bold text-gray-900">{{ t('shared_runs.title') }}</h1>
        <button v-if="signedIn" @click="openCreateModal" class="bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-800 transition">
          {{ t('shared_runs.create') }}
        </button>
      </div>

      <!-- Filters -->
      <div class="mb-6 bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div class="flex flex-wrap items-center gap-4">
          <!-- Date range -->
          <div class="flex items-center gap-2">
            <label class="text-xs text-gray-500">{{ t('shared_runs.filter_date') }}:</label>
            <input v-model="filterDateFrom" type="date"
                   class="text-xs border border-gray-300 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500" />
            <span class="text-xs text-gray-400">—</span>
            <input v-model="filterDateTo" type="date"
                   class="text-xs border border-gray-300 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>

          <!-- My runs filter -->
          <div v-if="signedIn" class="flex gap-1">
            <button @click="filterMy = 'all'"
                    :class="['px-3 py-1.5 text-xs font-medium rounded-lg transition', filterMy === 'all' ? 'bg-blue-700 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200']">
              {{ t('shared_runs.filter_all_runs') }}
            </button>
            <button @click="filterMy = 'mine'"
                    :class="['px-3 py-1.5 text-xs font-medium rounded-lg transition', filterMy === 'mine' ? 'bg-blue-700 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200']">
              {{ t('shared_runs.filter_my_runs') }}
            </button>
            <button @click="filterMy = 'joined'"
                    :class="['px-3 py-1.5 text-xs font-medium rounded-lg transition', filterMy === 'joined' ? 'bg-blue-700 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200']">
              {{ t('shared_runs.filter_joined') }}
            </button>
          </div>

          <!-- Distance range -->
          <div class="flex items-center gap-2">
            <label class="text-xs text-gray-500">{{ t('shared_runs.filter_distance') }}:</label>
            <input v-model.number="filterDistanceMin" type="number" min="0" step="1" :placeholder="t('shared_runs.filter_from')"
                   class="w-16 text-xs border border-gray-300 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500" />
            <span class="text-xs text-gray-400">—</span>
            <input v-model.number="filterDistanceMax" type="number" min="0" step="1" :placeholder="t('shared_runs.filter_to')"
                   class="w-16 text-xs border border-gray-300 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500" />
            <span class="text-xs text-gray-400">{{ t('shared_runs.km') }}</span>
          </div>

          <!-- Pace range -->
          <div class="flex items-center gap-2">
            <label class="text-xs text-gray-500">{{ t('shared_runs.filter_pace') }}:</label>
            <input v-model="filterPaceMin" type="text" placeholder="3:00" pattern="\d{1,2}:\d{2}"
                   class="w-16 text-xs border border-gray-300 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500" />
            <span class="text-xs text-gray-400">—</span>
            <input v-model="filterPaceMax" type="text" placeholder="7:00" pattern="\d{1,2}:\d{2}"
                   class="w-16 text-xs border border-gray-300 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500" />
            <span class="text-xs text-gray-400">{{ t('shared_runs.min_km') }}</span>
          </div>

          <span class="text-xs text-gray-400 ml-auto">{{ filteredRuns.length }} / {{ runs.length }}</span>
        </div>
      </div>

      <!-- Map -->
      <div v-show="runsWithCoords.length > 0" class="mb-8 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden map-container">
        <div ref="mapContainer" style="height: 320px; width: 100%;"></div>
      </div>

      <!-- Flash message -->
      <div v-if="flash" :class="['mb-4 px-4 py-3 rounded-lg text-sm', flash.type === 'success' ? 'bg-blue-100 text-blue-800' : 'bg-red-100 text-red-800']">
        {{ flash.message }}
      </div>

      <!-- Runs list -->
      <div v-if="filteredRuns.length === 0" class="text-center py-12 text-gray-500">
        {{ t('shared_runs.no_runs') }}
      </div>

      <div class="grid gap-4">
        <div v-for="run in filteredRuns" :key="run.id" :id="`run-${run.id}`" :class="['rounded-xl shadow-sm border p-6 transition', isPast(run) ? 'bg-gray-50 border-gray-200 opacity-60' : 'bg-white border-gray-200 hover:shadow-md']">
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <div class="flex items-center gap-3 mb-3">
                <span class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {{ formatDate(run.date) }}
                </span>
                <span class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {{ formatTime(run.date) }}
                </span>
              </div>

              <div class="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-3">
                <div v-if="run.distance">
                  <p class="text-xs text-gray-500">{{ t('shared_runs.distance') }}</p>
                  <p class="text-sm font-semibold text-gray-900">{{ run.distance }} {{ t('shared_runs.km') }}</p>
                </div>
                <div v-if="run.pace">
                  <p class="text-xs text-gray-500">{{ t('shared_runs.pace') }}</p>
                  <p class="text-sm font-semibold text-gray-900">{{ run.pace }} {{ t('shared_runs.min_km') }}</p>
                </div>

                <div>
                  <p class="text-xs text-gray-500">{{ t('shared_runs.organizer') }}</p>
                  <p v-if="signedIn && run.organizer.id !== userId" @click="openChatWith(run.organizer)" class="text-sm font-semibold text-gray-900 hover:text-blue-700 cursor-pointer">{{ run.organizer.name || run.organizer.email }}</p>
                  <p v-else class="text-sm font-semibold text-gray-900">{{ run.organizer.name || run.organizer.email }}</p>
                  <!-- Organizer: toggle phone visibility -->
                  <button v-if="run.organizer.id === userId && run.organizer_has_phone"
                          @click="togglePhone(run)"
                          :class="['mt-1 text-xs font-medium cursor-pointer', run.phone_visible ? 'text-blue-700' : 'text-gray-400']">
                    {{ run.phone_visible ? t('shared_runs.phone_shown') : t('shared_runs.phone_hidden') }}
                  </button>
                  <!-- Participant: see phone if visible -->
                  <a v-if="run.organizer.id !== userId && run.organizer.phone && isParticipant(run)"
                     :href="'tel:' + run.organizer.phone"
                     class="mt-1 inline-flex items-center gap-1 text-xs text-blue-700 hover:text-blue-900">
                    <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg>
                    {{ run.organizer.phone }}
                  </a>
                </div>
              </div>

              <div>
                <p class="text-xs text-gray-500 mb-1">{{ t('shared_runs.participants') }} ({{ run.participants.length }}):</p>
                <ul v-if="run.participants.length > 0" class="space-y-1">
                  <li v-for="p in run.participants" :key="p.id" class="flex items-center gap-2 text-sm text-gray-700">
                    <span class="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-xs font-medium text-blue-800 shrink-0">
                      {{ (p.first_name || p.name).charAt(0).toUpperCase() }}
                    </span>
                    <span v-if="signedIn && p.id !== userId" @click="openChatWith({ id: p.id, name: p.name })" class="hover:text-blue-700 cursor-pointer">{{ p.name }}</span>
                    <span v-else>{{ p.name }}</span>
                    <!-- Participant toggles own phone visibility -->
                    <button v-if="p.id === userId && p.has_phone"
                            @click="toggleParticipantPhone(run)"
                            :class="['text-xs cursor-pointer', p.phone_visible ? 'text-blue-700' : 'text-gray-400']">
                      {{ p.phone_visible ? t('shared_runs.phone_shown') : t('shared_runs.phone_hidden') }}
                    </button>
                    <!-- Organizer sees phone if participant consented -->
                    <a v-if="p.phone && run.organizer.id === userId"
                       :href="'tel:' + p.phone"
                       class="inline-flex items-center gap-1 text-xs text-blue-700 hover:text-blue-900">
                      <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg>
                      {{ p.phone }}
                    </a>
                  </li>
                </ul>
                <p v-else class="text-xs text-gray-400">{{ t('shared_runs.no_participants') }}</p>
              </div>

              <a :href="'/shared_runs/' + run.id"
                 class="mt-3 inline-block text-sm text-blue-700 font-medium hover:text-blue-900 hover:underline">
                {{ t('shared_runs.details') }} &rarr;
              </a>
            </div>

            <div v-if="signedIn" class="flex flex-col gap-2 ml-4">
              <button v-if="!isPast(run) && !isParticipant(run) && run.organizer.id !== userId" @click="joinRun(run)"
                      class="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition">
                {{ t('shared_runs.join') }}
              </button>
              <button v-if="!isPast(run) && isParticipant(run)" @click="leaveRun(run)"
                      class="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition">
                {{ t('shared_runs.leave') }}
              </button>
              <template v-if="run.organizer.id === userId || userAdmin">
                <button @click="openEditModal(run)" class="px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 transition">
                  {{ t('shared_runs.edit') }}
                </button>
                <button @click="confirmDelete(run)" class="px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition">
                  {{ t('shared_runs.delete') }}
                </button>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Create/Edit Modal -->
    <div v-if="showModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50" @click.self="closeModal">
      <div class="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
        <h2 class="text-lg font-bold text-gray-900 mb-6">
          {{ editing ? t('shared_runs.edit_run') : t('shared_runs.new_run') }}
        </h2>

        <div v-if="formErrors.length" class="mb-4 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
          <p v-for="error in formErrors" :key="error" class="text-sm text-red-700">{{ error }}</p>
        </div>

        <form @submit.prevent="submitForm" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">{{ t('shared_runs.date') }}</label>
            <input v-model="form.date" type="datetime-local" required
                   class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm" />
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">{{ t('shared_runs.distance') }} ({{ t('shared_runs.km') }})</label>
              <input v-model="form.distance" type="number" step="0.01" min="0"
                     class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm" />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">{{ t('shared_runs.pace') }} ({{ t('shared_runs.min_km') }})</label>
              <input v-model="form.pace" type="text" placeholder="5:30" pattern="\d{1,2}:\d{2}"
                     class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm" />
            </div>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">{{ t('shared_runs.pick_location') }}</label>
            <div ref="formMapContainer" style="height: 200px; width: 100%;" class="rounded-lg border border-gray-300"></div>
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">{{ t('shared_runs.latitude') }}</label>
              <input v-model="form.latitude" type="number" step="0.0000001" readonly
                     class="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm" />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">{{ t('shared_runs.longitude') }}</label>
              <input v-model="form.longitude" type="number" step="0.0000001" readonly
                     class="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm" />
            </div>
          </div>

          <div class="flex justify-end gap-3 pt-4">
            <button type="button" @click="closeModal"
                    class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition">
              {{ t('shared_runs.cancel') }}
            </button>
            <button type="submit" :disabled="submitting"
                    class="px-4 py-2 text-sm font-medium text-white bg-blue-700 rounded-lg hover:bg-blue-800 transition disabled:opacity-50">
              {{ submitting ? '...' : (editing ? t('shared_runs.save') : t('shared_runs.create')) }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50" @click.self="closeDeleteModal">
      <div class="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-6">
        <h2 class="text-lg font-bold text-gray-900 mb-2">{{ t('shared_runs.confirm_delete') }}</h2>
        <p class="text-sm text-gray-600 mb-6">{{ t('shared_runs.confirm_delete_message') }}</p>

        <div class="flex justify-end gap-3">
          <button @click="closeDeleteModal"
                  class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition">
            {{ t('shared_runs.cancel') }}
          </button>
          <button @click="deleteRun" :disabled="submitting"
                  class="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition disabled:opacity-50">
            {{ submitting ? '...' : t('shared_runs.delete') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, watch, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import NavBar from './NavBar.vue'
import L from 'leaflet'

// Fix Leaflet default marker icons with bundlers
delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png'
})

const props = defineProps({
  signedIn: Boolean,
  userName: String,
  userId: Number,
  userAdmin: Boolean,
  sharedRuns: Array,
  csrfToken: String
})

const { t, locale } = useI18n()

const showMenu = ref(false)
const logoSize = ref(120)
const runs = ref([...props.sharedRuns])
const today = new Date().toISOString().slice(0, 10)
const filterDateFrom = ref(today)
const filterDateTo = ref('')
const filterMy = ref('all')
const filterDistanceMin = ref(null)
const filterDistanceMax = ref(null)
const filterPaceMin = ref('')
const filterPaceMax = ref('')

function paceToSeconds(pace) {
  if (!pace) return null
  const parts = pace.split(':')
  if (parts.length !== 2) return null
  return parseInt(parts[0]) * 60 + parseInt(parts[1])
}

const filteredRuns = computed(() => {
  return runs.value.filter(run => {
    // Date range filter
    const runDate = new Date(run.date).toISOString().slice(0, 10)
    if (filterDateFrom.value && runDate < filterDateFrom.value) return false
    if (filterDateTo.value && runDate > filterDateTo.value) return false

    // My runs filter
    if (filterMy.value === 'mine' && run.organizer.id !== props.userId) return false
    if (filterMy.value === 'joined' && !run.participants.some(p => p.id === props.userId)) return false

    // Distance range
    if (run.distance) {
      const d = parseFloat(run.distance)
      if (filterDistanceMin.value && d < filterDistanceMin.value) return false
      if (filterDistanceMax.value && d > filterDistanceMax.value) return false
    } else {
      if (filterDistanceMin.value || filterDistanceMax.value) return false
    }

    // Pace range
    if (run.pace) {
      const p = paceToSeconds(run.pace)
      const pMin = paceToSeconds(filterPaceMin.value)
      const pMax = paceToSeconds(filterPaceMax.value)
      if (p !== null) {
        if (pMin !== null && p < pMin) return false
        if (pMax !== null && p > pMax) return false
      }
    } else {
      if (filterPaceMin.value || filterPaceMax.value) return false
    }

    return true
  })
})
const mapContainer = ref(null)
const formMapContainer = ref(null)
let map = null
let markers = []
let formMap = null
let formMarker = null

const runsWithCoords = computed(() =>
  filteredRuns.value.filter(r => r.latitude && r.longitude)
)

function initMap() {
  if (!mapContainer.value || runsWithCoords.value.length === 0) return

  if (map) {
    map.remove()
    map = null
  }

  const firstRun = runsWithCoords.value[0]
  map = L.map(mapContainer.value).setView([parseFloat(firstRun.latitude), parseFloat(firstRun.longitude)], 13)

  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
  }).addTo(map)

  setTimeout(() => map.invalidateSize(), 100)
  updateMarkers()
}

function updateMarkers() {
  if (!map) return

  markers.forEach(m => map.removeLayer(m))
  markers = []
  map.off('popupopen')

  const greyIcon = L.icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-grey.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
    iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41]
  })
  const greenIcon = L.icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-blue.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
    iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41]
  })

  runsWithCoords.value.forEach(run => {
    const past = new Date(run.date) < new Date()
    const marker = L.marker([parseFloat(run.latitude), parseFloat(run.longitude)], {
      icon: past ? greyIcon : greenIcon,
      opacity: past ? 0.5 : 1
    }).addTo(map)
    const organizer = run.organizer.name || run.organizer.email
    const dateStr = new Date(run.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
    const isJoined = run.participants.some(p => p.id === props.userId)
    const isOrganizer = run.organizer.id === props.userId

    let popup = `<div style="min-width:160px">
      <strong>${dateStr}</strong><br>
      <span style="color:#666">${t('shared_runs.organizer')}: ${organizer}</span><br>`
    if (run.distance) popup += `${t('shared_runs.distance')}: ${run.distance} ${t('shared_runs.km')}<br>`
    if (run.pace) popup += `${t('shared_runs.pace')}: ${run.pace} ${t('shared_runs.min_km')}<br>`
    popup += `${t('shared_runs.participants')}: ${run.participants.length}`
    if (!past && props.signedIn && !isJoined && !isOrganizer) {
      popup += `<br><button class="leaflet-join-btn" data-run-id="${run.id}" style="margin-top:6px;padding:4px 10px;background:#2563eb;color:#fff;border:none;border-radius:6px;cursor:pointer;font-size:12px;font-weight:500">${t('shared_runs.join')}</button>`
    }
    popup += `<br><a href="#run-${run.id}" class="leaflet-detail-link" data-run-id="${run.id}" style="display:inline-block;margin-top:6px;color:#1d4ed8;font-size:12px;font-weight:500;text-decoration:underline;cursor:pointer">${t('shared_runs.details')}</a>`
    popup += `</div>`
    marker.bindPopup(popup)
    markers.push(marker)
  })

  // Attach popup event listeners
  map.on('popupopen', (e) => {
    const container = e.popup.getElement()
    const joinBtn = container.querySelector('.leaflet-join-btn')
    if (joinBtn) {
      joinBtn.addEventListener('click', () => {
        const runId = parseInt(joinBtn.dataset.runId)
        const run = runs.value.find(r => r.id === runId)
        if (run) joinRun(run)
      })
    }
    const detailLink = container.querySelector('.leaflet-detail-link')
    if (detailLink) {
      detailLink.addEventListener('click', (ev) => {
        ev.preventDefault()
        const runId = parseInt(detailLink.dataset.runId)
        const el = document.getElementById(`run-${runId}`)
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      })
    }
  })

  if (markers.length > 1) {
    const group = L.featureGroup(markers)
    map.fitBounds(group.getBounds().pad(0.1))
  }
}

onMounted(() => {
  if (runsWithCoords.value.length > 0) {
    initMap()
  }
})

watch(runsWithCoords, () => {
  nextTick(() => {
    if (runsWithCoords.value.length > 0) {
      if (!map) {
        initMap()
      } else {
        updateMarkers()
      }
    }
  })
})

const showModal = ref(false)
const showDeleteModal = ref(false)
const editing = ref(false)
const editingId = ref(null)
const submitting = ref(false)
const formErrors = ref([])
const deleteTarget = ref(null)
const flash = ref(null)

const form = reactive({
  date: '',
  pace: '',
  distance: '',
  latitude: '',
  longitude: ''
})

function formatDate(dateStr) {
  const date = new Date(dateStr)
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

function formatTime(dateStr) {
  const date = new Date(dateStr)
  return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
}

function isPast(run) {
  return new Date(run.date) < new Date()
}

function openChatWith(user) {
  window.dispatchEvent(new CustomEvent('sidebar:open-chat', { detail: { id: user.id, name: user.name } }))
}

function isParticipant(run) {
  return run.participants.some(p => p.id === props.userId)
}

function showFlash(message, type = 'success') {
  flash.value = { message, type }
  setTimeout(() => { flash.value = null }, 4000)
}

function resetForm() {
  form.date = ''
  form.pace = ''
  form.distance = ''
  form.latitude = ''
  form.longitude = ''
  formErrors.value = []
}

function initFormMap() {
  setTimeout(() => {
    if (!formMapContainer.value) return

    if (formMap) {
      formMap.remove()
      formMap = null
      formMarker = null
    }

    const lat = parseFloat(form.latitude) || 47.0105
    const lng = parseFloat(form.longitude) || 28.8638

    formMap = L.map(formMapContainer.value).setView([lat, lng], 13)

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
    }).addTo(formMap)

    if (form.latitude && form.longitude) {
      formMarker = L.marker([lat, lng]).addTo(formMap)
    }

    formMap.on('click', (e) => {
      form.latitude = e.latlng.lat.toFixed(7)
      form.longitude = e.latlng.lng.toFixed(7)

      if (formMarker) {
        formMarker.setLatLng(e.latlng)
      } else {
        formMarker = L.marker(e.latlng).addTo(formMap)
      }
    })

    formMap.invalidateSize()
  }, 100)
}

function openCreateModal() {
  editing.value = false
  editingId.value = null
  resetForm()
  showModal.value = true
  initFormMap()
}

function openEditModal(run) {
  editing.value = true
  editingId.value = run.id
  form.date = new Date(run.date).toISOString().slice(0, 16)
  form.pace = run.pace || ''
  form.distance = run.distance || ''
  form.latitude = run.latitude || ''
  form.longitude = run.longitude || ''
  formErrors.value = []
  showModal.value = true
  initFormMap()
}

function closeModal() {
  showModal.value = false
}

function confirmDelete(run) {
  deleteTarget.value = run
  showDeleteModal.value = true
}

function closeDeleteModal() {
  showDeleteModal.value = false
  deleteTarget.value = null
}

async function request(url, method, body = null) {
  const opts = {
    method,
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': props.csrfToken,
      'Accept': 'application/json'
    }
  }
  if (body) opts.body = JSON.stringify(body)
  return fetch(url, opts)
}

async function submitForm() {
  submitting.value = true
  formErrors.value = []

  const payload = {
    shared_run: {
      date: form.date,
      pace: form.pace || null,
      distance: form.distance || null,
      latitude: form.latitude || null,
      longitude: form.longitude || null
    }
  }

  try {
    let res
    if (editing.value) {
      res = await request(`/shared_runs/${editingId.value}`, 'PATCH', payload)
    } else {
      res = await request('/shared_runs', 'POST', payload)
    }

    if (res.ok) {
      const data = await res.json()
      if (editing.value) {
        const idx = runs.value.findIndex(r => r.id === editingId.value)
        if (idx !== -1) runs.value[idx] = data
        showFlash(t('shared_runs.updated'))
      } else {
        runs.value.unshift(data)
        showFlash(t('shared_runs.created_success'))
      }
      closeModal()
    } else {
      const data = await res.json()
      formErrors.value = data.errors || [t('shared_runs.error')]
    }
  } catch {
    formErrors.value = [t('shared_runs.error')]
  } finally {
    submitting.value = false
  }
}

async function deleteRun() {
  submitting.value = true

  try {
    const res = await request(`/shared_runs/${deleteTarget.value.id}`, 'DELETE')
    if (res.ok) {
      runs.value = runs.value.filter(r => r.id !== deleteTarget.value.id)
      showFlash(t('shared_runs.deleted'))
      closeDeleteModal()
    }
  } catch {
    showFlash(t('shared_runs.error'), 'error')
  } finally {
    submitting.value = false
  }
}

async function toggleParticipantPhone(run) {
  try {
    const res = await request(`/shared_runs/${run.id}/toggle_phone`, 'PATCH')
    if (res.ok) {
      const data = await res.json()
      const idx = runs.value.findIndex(r => r.id === run.id)
      if (idx !== -1) runs.value[idx] = data
    }
  } catch {
    showFlash(t('shared_runs.error'), 'error')
  }
}

async function togglePhone(run) {
  try {
    const res = await request(`/shared_runs/${run.id}`, 'PATCH', {
      shared_run: { phone_visible: !run.phone_visible }
    })
    if (res.ok) {
      const data = await res.json()
      const idx = runs.value.findIndex(r => r.id === run.id)
      if (idx !== -1) runs.value[idx] = data
    }
  } catch {
    showFlash(t('shared_runs.error'), 'error')
  }
}

async function joinRun(run) {
  try {
    const res = await request(`/shared_runs/${run.id}/join`, 'POST')
    if (res.ok) {
      const data = await res.json()
      const idx = runs.value.findIndex(r => r.id === run.id)
      if (idx !== -1) runs.value[idx] = data
      showFlash(t('shared_runs.joined'))
    }
  } catch {
    showFlash(t('shared_runs.error'), 'error')
  }
}

async function leaveRun(run) {
  try {
    const res = await request(`/shared_runs/${run.id}/leave`, 'DELETE')
    if (res.ok) {
      const data = await res.json()
      const idx = runs.value.findIndex(r => r.id === run.id)
      if (idx !== -1) runs.value[idx] = data
      showFlash(t('shared_runs.left'))
    }
  } catch {
    showFlash(t('shared_runs.error'), 'error')
  }
}
</script>
